import type { VaultProvider } from '../../vaults/fetchVaultsAll'
import type { VaultExitRoute } from '../../vaults/exitRoutes'
import type { VaultMarketExposure } from '../../vaults/exposure'
import type { EarnMarket } from '../../earn/types'
import { SDK_FRACTION_RATE_PROVIDERS } from '../../earn/fromVault'
import { VAULT_VENUE_PREFIX } from '../../earn/uid'
import type { CounterpartyTerms, TermAssetRef } from '../types'

/**
 * The normalized vault facts the builder reads.
 *
 * Deliberately NOT any one provider's vault type. The same vault reaches this
 * code in two shapes — the in-package per-provider object
 * (`SavingsVault`, `PendlePtMarket`, …) during a fetch, and the recorder's
 * flattened `/vaults/latest` row (`rates.*`, `tvl.*`, `providerMeta.*`) while
 * the worker proxies an origin response — exactly the split the lending
 * `TermSheetInput` already documents. One tolerant reader per shape normalizes
 * into this interface and the builder stays pure over it.
 *
 * Every field is optional except the three that key the row: a vault we cannot
 * identify is dropped upstream, and a vault whose numbers are missing still
 * gets a sheet that says so through `coverage`.
 */
export interface VaultTermInput {
  provider: VaultProvider
  chainId: string
  /** Share-token address, lowercased. The vault's identity. */
  address: string
  /** `vault.<provider>:<chainId>:<address>` — the earn uid. */
  vaultUid?: string
  name?: string
  brand?: string
  curatorName?: string

  asset?: TermAssetRef
  /** Curated user-facing explainer (`SavingsVault.description`). */
  description?: string

  // ── rate (PERCENT — package convention) ───────────────────────────────────
  supplyRate?: number
  rewardsRate?: number
  /** The provider's own all-in figure, when it publishes one. */
  totalRate?: number
  /**
   * Yearn's `isForwardApr`: is the published number a PROJECTION or a
   * MEASUREMENT? Yearn ships both under one field name, so this flag is the
   * only thing that distinguishes them.
   */
  isForwardApr?: boolean
  /** Lagoon's `aprWindow` — `weekly` | `monthly` | `yearly` | `inception`. */
  aprWindow?: string

  // ── size ──────────────────────────────────────────────────────────────────
  totalAssets?: number
  totalAssetsUsd?: number
  liquidity?: number
  liquidityUsd?: number
  /** `liquidity / totalAssets`, when the provider computes it itself. */
  instantLiquidityRatio?: number
  totalBorrowed?: number
  /** Gearbox `expectedLiquidity` — the utilization denominator. */
  expectedLiquidity?: number

  // ── exit ──────────────────────────────────────────────────────────────────
  withdrawalMode?: string
  withdrawalCooldownSeconds?: number
  withdrawFeeBps?: number
  /**
   * The row's per-leg exit routes, passed straight through to
   * `SupplyExitTerms.routes`. Present on every vault row; when a row predates
   * the field the builder derives the same list from the flat fields, so the
   * sheet never loses the split.
   */
  exitRoutes?: VaultExitRoute[]
  /** Performance fee on yield, as the provider reports it. */
  fee?: number
  /**
   * Management fee on ASSETS, percent per year. A separate field rather than a
   * second use of `fee` because the two answer different questions: a
   * performance fee costs nothing in a flat year, a management fee is owed
   * regardless. Upshift publishes both, up to 20 % + 2 % on one vault.
   *
   * Report the EFFECTIVE rate — 0 where a waiver is live — since that is what
   * is charged today, and the mutability of the schedule is already carried by
   * `FeeTerm.mutable`.
   */
  managementFee?: number
  /** Pendle's AMM swap fee, a FRACTION (`0.0005`), charged on BOTH legs. */
  swapFeeRate?: number
  /** Signed bps gap between redemption value and fundamental (Yield Basis TRD). */
  redemptionDiscountBps?: number
  /** `fee-or-queued` vaults only — is the instant leg switched on at all? */
  instantRedeemEnabled?: boolean

  /**
   * Seconds a fresh deposit earns nothing (Frankencoin `INTEREST_DELAY`).
   * NOT a withdrawal lock — see `RateTerms.warmupSecs`.
   */
  yieldWarmupSeconds?: number
  /**
   * Does the accrual COMPOUND, or is it linear?
   *
   * Defaults to compounding, which is right for any vault whose share price
   * grows continuously. Frankencoin's savings module is linear
   * (`Δticks × saved / 1e6 / 365 days`) and only compounds when someone
   * happens to call `refresh`, so labelling it per-second would imply an APY
   * ~2 % relative above what it pays.
   */
  accrual?: 'linear' | 'compounding'
  /**
   * Does a deposit need an ERC-20 approval?
   *
   * Defaults to true — nearly every vault pulls with `transferFrom`. False for
   * Frankencoin's savings modules, whose underlying grants a registered minter
   * an implicit infinite allowance, so the deposit route emits no approval and
   * a sheet claiming one would contradict the envelope beside it.
   */
  needsDepositApproval?: boolean

  // ── availability ──────────────────────────────────────────────────────────
  isMintable?: boolean
  /** Raw base units. `undefined` = uncapped, `'0'` = full. */
  depositCapacity?: string
  /** Raw base units (TermMax `supplyCap`). */
  supplyCap?: string
  paused?: boolean
  depositsPaused?: boolean
  withdrawalsPaused?: boolean
  /** HyperCore — the vault is closed to new deposits. */
  isClosed?: boolean
  /** Morpho — the vault is on the curator's whitelist. */
  whitelisted?: boolean

  // ── maturity (pendle) ─────────────────────────────────────────────────────
  /** Unix seconds. */
  expiry?: number

  // ── governance ────────────────────────────────────────────────────────────
  timelock?: number
  /** Addresses that can REALLOCATE the vault between markets, typically with
   *  no timelock — the curation power that moves a depositor's exposure
   *  between blocks. */
  allocators?: string[]
  owner?: string
  curator?: string
  guardian?: string
  feeRecipient?: string

  // ── trust ─────────────────────────────────────────────────────────────────
  /**
   * Curated loss-waterfall / backing classification. Absent ⇒ NOT curated, and
   * the builder reports that through `coverage.pending` rather than asserting a
   * comfortable default.
   */
  solvency?: CounterpartyTerms['solvency']
  /** NAV feed address, for the share prices published by an operator. */
  navOracle?: string
  /** `volatile` ⇒ the share price can fall in asset terms. */
  yieldProfile?: string

  // ── backing ───────────────────────────────────────────────────────────────
  exposures?: VaultMarketExposure[]
}

// ---------------------------------------------------------------------------
// tolerant accessors
// ---------------------------------------------------------------------------

const num = (v: unknown): number | undefined => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined

const bool = (v: unknown): boolean | undefined =>
  typeof v === 'boolean' ? v : undefined

/** Non-empty array of non-empty strings, or nothing. */
/**
 * Exit routes arrive from three origins — a live provider row, an earn row's
 * `providerMeta`, and a previously-built sheet — and only the first is
 * statically typed. Validate structurally rather than casting: a route
 * without an `id` and a `kind` is not renderable, and passing it through
 * would put an unlabelled leg on a term sheet.
 */
const routes = (v: unknown): VaultExitRoute[] | undefined => {
  if (!Array.isArray(v)) return undefined
  const out = v.filter(
    (r): r is VaultExitRoute =>
      !!r &&
      typeof r === 'object' &&
      typeof (r as VaultExitRoute).id === 'string' &&
      typeof (r as VaultExitRoute).kind === 'string',
  )
  return out.length > 0 ? out : undefined
}

const strArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined
  const out = v.filter((x): x is string => typeof x === 'string' && x !== '')
  return out.length > 0 ? out : undefined
}

/** Raw base-unit integer string → token units, without touching float64. */
const fromRaw = (
  raw: unknown,
  decimals: number | undefined,
): number | undefined => {
  const s = raw == null ? undefined : String(raw)
  if (!s || !/^\d+$/.test(s) || decimals == null) return undefined
  const n = Number(s) / 10 ** decimals
  return Number.isFinite(n) ? n : undefined
}

/**
 * The in-package per-provider vault object, as a permissive union.
 *
 * Typed as an index signature rather than `SavingsVault | PendlePtMarket | …`
 * on purpose: the 15 provider types share no common base beyond
 * `VaultClassificationFields`, and a discriminated union would have to be
 * updated in lockstep with every provider — the exact coupling the traits table
 * exists to avoid. Every read below is guarded.
 */
export type AnyVaultRow = object

/**
 * Rates off an in-package vault object — the one place the providers genuinely
 * disagree on both FIELD NAMES and UNITS.
 *
 * Thirteen providers publish `supplyRate` / `rewardsRate` / `depositRate` in
 * PERCENT. The two trading providers publish neither: GMX has
 * `apy`/`baseApy`/`bonusApr` and HyperCore has `apr`, all **fractions**. Reading
 * the common names off them yields `undefined`, so both would report a flat 0 %
 * — a trading vault silently claiming it pays nothing — and reading them
 * without scaling would report 0.16 % for a 16 % return.
 *
 * The scaling belongs HERE and only here: it is a property of the SDK shape.
 * The recorder runs its own `pct()` pass before `/vaults/latest`, which is why
 * `vaultTermInputFromSourceRow` must NOT scale, and why the two conventions have
 * carried separate constants since long before term sheets existed.
 */
function sdkRates(
  row: Record<string, unknown>,
  provider: VaultProvider,
): Pick<VaultTermInput, 'supplyRate' | 'rewardsRate' | 'totalRate'> {
  if (!SDK_FRACTION_RATE_PROVIDERS.has(provider)) {
    return {
      supplyRate: num(row.supplyRate),
      rewardsRate: num(row.rewardsRate),
      totalRate: num(row.depositRate),
    }
  }
  const pct = (v: unknown) => {
    const n = num(v)
    return n === undefined ? undefined : n * 100
  }
  return {
    // GMX splits fee APY from incentive APR; HyperCore reports one number.
    supplyRate: pct(row.baseApy ?? row.apr),
    rewardsRate: pct(row.bonusApr),
    totalRate: pct(row.apy ?? row.apr),
  }
}

/**
 * Normalize an in-package vault object (the `getVaultPublicDataAll` shape).
 */
export function toVaultTermInput(
  vault: AnyVaultRow,
  provider: VaultProvider,
  chainId: string,
): VaultTermInput | undefined {
  // A provider interface is not assignable to an index signature in TS, and
  // every read below is already guarded — one cast at the boundary keeps the
  // 15 provider types out of this file's imports.
  const row = vault as Record<string, unknown>
  const address = str(row.address)?.toLowerCase()
  if (!address) return undefined

  const assetDecimals = num(row.assetDecimals) ?? num(row.decimals)
  const asset = row.asset as Record<string, unknown> | undefined

  return {
    provider,
    chainId,
    address,
    vaultUid: `vault.${provider}:${chainId}:${address}`,
    name: str(row.displayName) ?? str(row.name),
    brand: str(row.brand),
    curatorName: str(row.curatorName),
    description: str(row.description),

    asset: {
      chainId,
      address: str(row.underlying)?.toLowerCase() ?? '',
      symbol: str(asset?.symbol),
      name: str(asset?.name),
      decimals: assetDecimals,
      assetGroup: str(asset?.assetGroup),
      logoURI: str(asset?.logoURI),
    },

    ...sdkRates(row, provider),
    isForwardApr: bool(row.isForwardApr),
    aprWindow: str(row.aprWindow),

    // GMX / HyperCore / Pendle report USD only and set the token-denominated
    // fields to their USD figure — `totalAssetsFormatted` is the cross-provider
    // magnitude either way, so prefer it over re-deriving from raw.
    totalAssets:
      num(row.totalAssetsFormatted) ?? fromRaw(row.totalAssets, assetDecimals),
    totalAssetsUsd: num(row.totalAssetsUsd) ?? num(row.tvlUsd),
    liquidity:
      num(row.liquidityFormatted) ?? fromRaw(row.liquidity, assetDecimals),
    liquidityUsd: num(row.liquidityUsd),
    instantLiquidityRatio: num(row.instantLiquidityRatio),
    totalBorrowed: fromRaw(row.totalBorrowed, assetDecimals),
    expectedLiquidity: fromRaw(row.expectedLiquidity, assetDecimals),

    withdrawalMode: str(row.withdrawalMode),
    withdrawalCooldownSeconds: num(row.withdrawalCooldownSeconds),
    withdrawFeeBps: num(row.withdrawFeeBps),
    exitRoutes: routes(row.exitRoutes),
    fee: num(row.fee),
    managementFee: num(row.managementFee),
    swapFeeRate: num(row.feeRate),
    redemptionDiscountBps: num(row.redemptionDiscountBps),
    instantRedeemEnabled: bool(row.instantRedeemEnabled),
    yieldWarmupSeconds: num(row.yieldWarmupSeconds),
    accrual: str(row.accrual) as 'linear' | 'compounding' | undefined,
    needsDepositApproval: bool(row.needsDepositApproval),

    isMintable: bool(row.isMintable),
    depositCapacity: str(row.depositCapacity),
    supplyCap: str(row.supplyCap),
    paused: bool(row.isPaused),
    depositsPaused: bool(row.isDepositPaused),
    withdrawalsPaused: bool(row.isWithdrawalPaused),
    isClosed: bool(row.isClosed),
    whitelisted: bool(row.whitelisted),

    expiry: num(row.expiry),

    timelock: num(row.timelock),
    allocators: strArray(row.allocators),
    owner: str(row.owner),
    curator: str(row.curator),
    guardian: str(row.guardian),
    feeRecipient: str(row.feeRecipient),

    solvency: str(row.solvency) as CounterpartyTerms['solvency'] | undefined,
    navOracle: str(row.navOracle),
    yieldProfile: str(row.yieldProfile),

    exposures: Array.isArray(row.exposures)
      ? (row.exposures as VaultMarketExposure[])
      : undefined,
  }
}

/**
 * Normalize an already-normalized `EarnMarket` back into term-sheet input.
 *
 * The third door, and it exists for a caching reason rather than a shape one:
 * `/v1/data/earn` caches the whole MERGED listing and slices pages out of it,
 * so baking a sheet into every row would inflate the cached blob by thousands
 * of sheets to serve fifty. Rebuilding from the served page costs nothing and
 * keeps the cache lean.
 *
 * Everything the builder needs survived normalization: the row keeps its
 * `providerMeta` verbatim, its exposures under `refs`, and its rates — including
 * the fee — in normalized percent.
 *
 * Returns `undefined` for a LENDING row. Those are not vaults and must go
 * through `buildTermSheet`, which has the borrow side.
 */
export function vaultTermInputFromEarnMarket(
  m: EarnMarket,
): VaultTermInput | undefined {
  if (m.venueKind !== 'vault') return undefined
  const provider = m.venue.startsWith(VAULT_VENUE_PREFIX)
    ? (m.venue.slice(VAULT_VENUE_PREFIX.length) as VaultProvider)
    : undefined
  if (!provider) return undefined

  const meta: Record<string, unknown> = m.providerMeta ?? {}

  return {
    provider,
    chainId: m.chainId,
    address: m.ref,
    vaultUid: m.earnUid,
    name: m.name,
    brand: m.brand,
    curatorName: m.brand,
    description: str(meta.description),

    asset: {
      chainId: m.chainId,
      address: m.asset.address,
      symbol: m.asset.symbol,
      decimals: m.asset.decimals,
      assetGroup: m.asset.assetGroup,
      logoURI: m.logoURI,
    },

    supplyRate: m.rate.base,
    rewardsRate: m.rate.rewards,
    totalRate: m.rate.total,
    isForwardApr: bool(meta.isForwardApr),
    aprWindow: str(meta.aprWindow),

    totalAssets: m.tvl.formatted,
    totalAssetsUsd: m.tvl.usd,
    liquidity: m.liquidity?.formatted,
    liquidityUsd: m.liquidity?.usd,
    instantLiquidityRatio: num(meta.instantLiquidityRatio),
    totalBorrowed: num(meta.totalBorrowed),
    expectedLiquidity: num(meta.expectedLiquidity),

    withdrawalMode: m.exit.mode,
    withdrawalCooldownSeconds: m.exit.cooldownSecs,
    withdrawFeeBps: m.exit.feeBps,
    exitRoutes: routes(m.exit.routes),
    fee: m.rate.fee,
    managementFee: num(meta.managementFee),
    swapFeeRate: num(meta.feeRate),
    redemptionDiscountBps: num(meta.redemptionDiscountBps),
    instantRedeemEnabled: bool(meta.instantRedeemEnabled),
    yieldWarmupSeconds: num(meta.yieldWarmupSeconds),
    accrual: str(meta.accrual) as 'linear' | 'compounding' | undefined,
    needsDepositApproval: bool(meta.needsDepositApproval),

    // `canDeposit` already folds in the cap, the pause and the gate, so it is
    // the authoritative answer — but the REASON is what a disabled CTA renders,
    // and that only survives in the raw fields.
    isMintable: m.availability.canDeposit ? undefined : false,
    depositCapacity: m.depositCapacity,
    supplyCap: str(meta.supplyCap),
    paused: m.availability.gating === 'paused' ? true : undefined,
    withdrawalsPaused: m.availability.canWithdraw ? undefined : true,
    isClosed: bool(meta.isClosed),
    whitelisted: bool(meta.whitelisted),

    expiry: m.maturity?.maturity,

    timelock: num(meta.timelock),
    allocators: strArray(meta.allocators),
    owner: str(meta.owner),
    curator: str(meta.curator),
    guardian: str(meta.guardian),
    feeRecipient: str(meta.feeRecipient),

    solvency: m.risk?.counterparty,
    navOracle: str(meta.navOracle),
    yieldProfile: m.risk?.yieldProfile,

    exposures: m.refs?.exposures,
  }
}

/**
 * Split the recorder's `rates` block into base and rewards.
 *
 * **`depositRate` is the SUM, not the base.** Every provider type documents it
 * as `supplyRate + rewardsRate`, so reading it as the base leg and then adding
 * `rewardsRate` on top counts the incentives twice — a vault paying 4 % + 1 %
 * reports 6 %. That is invisible on any row where rewards are zero, which is
 * most of them, and silently wrong on exactly the rows an incentive-hunting
 * user is looking at.
 *
 * Where `supplyRate` is absent the base is RECOVERED from the documented
 * identity (`deposit − rewards`) rather than guessed.
 */
function originRates(
  rates: Record<string, unknown>,
): Pick<VaultTermInput, 'supplyRate' | 'rewardsRate' | 'totalRate'> {
  const supply = num(rates.supplyRate)
  const rewards = num(rates.rewardsRate)
  const deposit = num(rates.depositRate)
  return {
    supplyRate:
      supply ??
      (deposit !== undefined && rewards !== undefined
        ? deposit - rewards
        : deposit),
    rewardsRate: rewards,
    totalRate: num(rates.totalRate) ?? deposit,
  }
}

/**
 * Normalize a recorder `/vaults/latest` row.
 *
 * The origin flattens rates into `rates.*`, sizes into `tvl.*` / `liquidity.*`
 * and parks everything provider-specific under `providerMeta` — so the same
 * facts live at different paths than in the SDK shape, and several
 * (`expiry`, `withdrawalMode`, `depositCapacity`) exist ONLY in `providerMeta`.
 */
export function vaultTermInputFromSourceRow(
  row: Record<string, any>,
  chainId: string,
): VaultTermInput | undefined {
  const provider = str(row.provider) as VaultProvider | undefined
  const address = str(row.vaultAddress)?.toLowerCase()
  if (!provider || !address) return undefined

  const meta: Record<string, unknown> = row.providerMeta ?? {}
  const rates: Record<string, unknown> = row.rates ?? {}
  const tvl: Record<string, unknown> = row.tvl ?? {}
  const liq: Record<string, unknown> = row.liquidity ?? {}
  const info: Record<string, unknown> = row.vaultInfo ?? {}
  const assetInfo = row.underlyingInfo?.asset as
    | Record<string, unknown>
    | undefined

  const assetDecimals =
    num(row.assetDecimals) ?? num(assetInfo?.decimals) ?? num(row.decimals)

  return {
    provider,
    chainId,
    address,
    vaultUid: `vault.${provider}:${chainId}:${address}`,
    name: str(info.name) ?? str(row.displayName) ?? str(row.name),
    brand: str(row.curatorName) ?? str(meta.brand),
    curatorName: str(row.curatorName),
    description: str(meta.description),

    asset: {
      chainId,
      address: str(row.underlying)?.toLowerCase() ?? '',
      symbol: str(assetInfo?.symbol),
      decimals: assetDecimals,
      assetGroup: str(info.assetGroup),
      logoURI: str(assetInfo?.logoURI),
    },

    ...originRates(rates),
    isForwardApr: bool(meta.isForwardApr),
    aprWindow: str(meta.aprWindow),

    totalAssets:
      num(tvl.totalAssetsFormatted) ?? fromRaw(tvl.totalAssets, assetDecimals),
    totalAssetsUsd: num(tvl.totalAssetsUsd),
    liquidity:
      num(liq.liquidityFormatted) ?? fromRaw(liq.liquidity, assetDecimals),
    liquidityUsd: num(liq.liquidityUsd),
    instantLiquidityRatio: num(meta.instantLiquidityRatio),
    totalBorrowed: fromRaw(meta.totalBorrowed, assetDecimals),
    expectedLiquidity: fromRaw(meta.expectedLiquidity, assetDecimals),

    withdrawalMode: str(meta.withdrawalMode),
    withdrawalCooldownSeconds: num(meta.withdrawalCooldownSeconds),
    withdrawFeeBps: num(meta.withdrawFeeBps),
    exitRoutes: routes(meta.exitRoutes),
    fee: num(rates.fee),
    managementFee: num(meta.managementFee),
    swapFeeRate: num(meta.feeRate),
    redemptionDiscountBps: num(meta.redemptionDiscountBps),
    instantRedeemEnabled: bool(meta.instantRedeemEnabled),
    yieldWarmupSeconds: num(meta.yieldWarmupSeconds),
    accrual: str(meta.accrual) as 'linear' | 'compounding' | undefined,
    needsDepositApproval: bool(meta.needsDepositApproval),

    isMintable: bool(meta.isMintable),
    depositCapacity: str(meta.depositCapacity) ?? str(meta.maxDeposit),
    supplyCap: str(meta.supplyCap),
    paused: bool(meta.paused) ?? bool(meta.isPaused),
    depositsPaused: bool(meta.isDepositPaused),
    withdrawalsPaused: bool(meta.isWithdrawalPaused),
    isClosed: bool(meta.isClosed),
    whitelisted: bool(meta.whitelisted),

    expiry: num(meta.expiry) ?? num(meta.maturity),

    timelock: num(meta.timelock),
    allocators: strArray(meta.allocators),
    owner: str(meta.owner),
    curator: str(meta.curator),
    guardian: str(meta.guardian),
    feeRecipient: str(meta.feeRecipient),

    solvency: str(meta.solvency) as CounterpartyTerms['solvency'] | undefined,
    navOracle: str(meta.navOracle),
    yieldProfile: str(info.yieldProfile),

    exposures: Array.isArray(row.exposures)
      ? (row.exposures as VaultMarketExposure[])
      : Array.isArray(meta.exposures)
        ? (meta.exposures as VaultMarketExposure[])
        : undefined,
  }
}
